/**
 * South African public holidays.
 *
 * Used by the business-day clock that backs the Bonus Qualifying and
 * SLA Compliance stats (see CLAUDE.md §21). Dates are listed in the local
 * South African calendar (YYYY-MM-DD) and represent the day on which the
 * holiday is *observed* (not necessarily the day it formally falls).
 *
 * Per the Public Holidays Act 36 of 1994, when a public holiday falls on
 * a Sunday it is observed on the following Monday. Saturday holidays are
 * NOT shifted. The list below applies that rule.
 *
 * ⚠️  REVIEW AND EXTEND ANNUALLY. Current coverage ends 31 Dec 2027.
 *     After that date the business-day clock will treat every weekday as
 *     a working day until this list is updated.
 */

export const SA_PUBLIC_HOLIDAYS = [
  // 2025
  '2025-01-01', // New Year's Day (Wed)
  '2025-03-21', // Human Rights Day (Fri)
  '2025-04-18', // Good Friday
  '2025-04-21', // Family Day (Easter Monday)
  '2025-04-28', // Freedom Day observed (27 Apr was Sunday)
  '2025-05-01', // Workers' Day (Thu)
  '2025-06-16', // Youth Day (Mon)
  '2025-08-09', // National Women's Day (Sat — not shifted)
  '2025-09-24', // Heritage Day (Wed)
  '2025-12-16', // Day of Reconciliation (Tue)
  '2025-12-25', // Christmas Day (Thu)
  '2025-12-26', // Day of Goodwill (Fri)

  // 2026
  '2026-01-01', // New Year's Day (Thu)
  '2026-03-21', // Human Rights Day (Sat — not shifted)
  '2026-04-03', // Good Friday
  '2026-04-06', // Family Day
  '2026-04-27', // Freedom Day (Mon)
  '2026-05-01', // Workers' Day (Fri)
  '2026-06-16', // Youth Day (Tue)
  '2026-08-10', // National Women's Day observed (9 Aug was Sunday)
  '2026-09-24', // Heritage Day (Thu)
  '2026-12-16', // Day of Reconciliation (Wed)
  '2026-12-25', // Christmas Day (Fri)
  '2026-12-26', // Day of Goodwill (Sat — not shifted)

  // 2027
  '2027-01-01', // New Year's Day (Fri)
  '2027-03-22', // Human Rights Day observed (21 Mar was Sunday)
  '2027-03-26', // Good Friday
  '2027-03-29', // Family Day
  '2027-04-27', // Freedom Day (Tue)
  '2027-05-01', // Workers' Day (Sat — not shifted)
  '2027-06-16', // Youth Day (Wed)
  '2027-08-09', // National Women's Day (Mon)
  '2027-09-24', // Heritage Day (Fri)
  '2027-12-16', // Day of Reconciliation (Thu)
  '2027-12-25', // Christmas Day (Sat — not shifted)
  '2027-12-27', // Day of Goodwill observed (26 Dec was Sunday)
];

const HOLIDAY_SET = new Set(SA_PUBLIC_HOLIDAYS);

/**
 * Return the Africa/Johannesburg calendar date for a Date or ISO string,
 * formatted as YYYY-MM-DD. SAST is a fixed offset (UTC+2, no DST), so we
 * can rely on Intl to give us the wall-clock date in that zone.
 */
export function toSADateString(date) {
  if (date == null) return null;
  const d = date instanceof Date ? date : new Date(date);
  if (Number.isNaN(d.getTime())) return null;
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Africa/Johannesburg',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(d);
  const y = parts.find((p) => p.type === 'year').value;
  const m = parts.find((p) => p.type === 'month').value;
  const day = parts.find((p) => p.type === 'day').value;
  return `${y}-${m}-${day}`;
}

/**
 * Is the given instant (Date or ISO string) a SA public holiday in SAST?
 */
export function isSAPublicHoliday(date) {
  const key = toSADateString(date);
  return key !== null && HOLIDAY_SET.has(key);
}
